'use client';

import { useEffect, useRef, useState } from 'react';
import Image, { StaticImageData } from 'next/image';

type Props = {
  posterSrc: string | StaticImageData;
  webmSrc?: string;
  mp4Src: string;
  alt?: string;
  preload?: boolean;
};

export default function HeroVideo({ posterSrc, webmSrc, mp4Src, alt = '', preload = true }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldPlay, setShouldPlay] = useState(false);

  useEffect(() => {
    // Respect user preferences
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dataSaver = (navigator as any).connection?.saveData === true;

    if (reducedMotion || dataSaver) {
      return undefined;
    }

    // Defer to idle or a short timeout to let LCP settle
    const LCP_SETTLE_TIMEOUT_MS = 1200;
    const start = () => setShouldPlay(true);

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(start, { timeout: 2000 });
      return undefined;
    } else {
      const t = setTimeout(start, LCP_SETTLE_TIMEOUT_MS);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!shouldPlay || !videoRef.current) {
      return;
    }
    // Once sources are attached, load & play
    const v = videoRef.current;
    v.load();
    v.play().catch(() => {
      /* autoplay may fail in rare cases; ignore */
    });
  }, [shouldPlay]);

  return (
    <div className="relative aspect-[23/47] w-60 overflow-hidden md:w-80">
      {/* Poster image is the LCP element */}
      <Image
        src={posterSrc}
        alt={alt}
        fill
        preload={preload}
        sizes="(min-width: 990px) 320px, 240px "
        className="object-cover"
      />

      {/* Video is layered above, but transparent until sources load */}
      <video
        ref={videoRef}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
          shouldPlay ? 'opacity-100' : 'opacity-0'
        }`}
        muted
        playsInline
        autoPlay
        loop
        preload="none"
        aria-hidden={alt === '' ? true : undefined}
      >
        {shouldPlay && (
          <>
            {webmSrc && <source src={webmSrc} type="video/webm" />}
            <source src={mp4Src} type="video/mp4" />
          </>
        )}
      </video>
    </div>
  );
}
